import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type FastApiService from 'walden-fast/services/fast-api';
import type UserService from 'walden-fast/services/user';
import type PostHogService from 'walden-fast/services/posthog';
import type { ClientCompany } from 'walden-fast/types/fast-backend/user';
import type { YearlyEmissionsReport } from 'walden-fast/types/yearly-emissions-report';
import downloadFile from 'walden-fast/utils/download-file';

export default class AppServicesWaldenGreenReportController extends Controller {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('user') declare userService: UserService;
  @service('posthog') declare postHogService: PostHogService;

  @tracked reportError = false;

  get clients() {
    return this.userService.user ? this.userService.user.clientCompanies : [];
  }

  get years() {
    return [2024];
  }

  @action
  hideError() {
    this.reportError = false;
  }

  downloadReport = task(async (year: number, client: ClientCompany) => {
    const isExternalUser = !this.userService.needsToImpersonate();

    const customPostHogEventData = {
      userType: isExternalUser ? 'external' : 'internal',
      client: client.displayName,
      year,
      succesful: false,
    };

    try {
      this.reportError = false;

      const result = await this.fastApiService.get<YearlyEmissionsReport>(
        '/walden-green/reports/yearly-emissions-report',
        {
          year,
          clientCompanyId: client.id,
        },
      );

      if (!result?.url) {
        throw new Error('No report URL received');
      }

      await downloadFile(result.url, result.fileName);

      customPostHogEventData.succesful = true;

      return result;
    } catch {
      this.reportError = true;
    } finally {
      this.postHogService.sendCustomEvent(
        'annual_report_download',
        customPostHogEventData,
      );
    }
  });
}
